import { Pressable, ScrollView } from "react-native";
import { Link, type Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { activationCameraKey, type CursorPager, type SignalSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { CursorPagerBar, countOf } from "./CursorPagerBar";

interface SignalsTableProps {
  readonly items: readonly SignalSummary[];
  /** Dim the rows while they are the previous page's, held until the next arrives. */
  readonly isStale: boolean;
  /** Where a signal's activation opens. */
  readonly activationHref: (ticketNo: string) => Href;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
}

const COLUMNS = [
  { label: "Received", style: "received" },
  { label: "Description", style: "description" },
  { label: "Site", style: "site" },
  { label: "Camera", style: "camera" },
  { label: "Activation", style: "ticket" },
  { label: "a3s signal", style: "signalId" },
] as const;

/**
 * Laid out like `ActivationsTable`, but the row is not a link: a signal has no
 * page of its own. Its activation, when it raised one, is the link.
 */
export function SignalsTable({
  items,
  isStale,
  activationHref,
  siteLabel,
  cameraLabel,
  pager,
  nextCursor,
  isFetching,
}: SignalsTableProps) {
  return (
    <View flex={1}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, isStale && styles.stale]}>
          <View style={[styles.row, styles.headerRow]}>
            {COLUMNS.map((column) => (
              <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                {column.label}
              </Text>
            ))}
          </View>
          {items.map((signal, index) => (
            // Not keyed by the a3s id alone: VA can post one clip twice, and
            // both copies reach Patriot as signals naming the same clip.
            <SignalRow
              key={`${signal.a3sSignalId}#${String(index)}`}
              signal={signal}
              activationHref={activationHref}
              siteLabel={siteLabel}
              cameraLabel={cameraLabel}
            />
          ))}
          {items.length === 0 && (
            <View style={styles.row}>
              <Text textStyle="sm/regular" color="textMuted">
                No more signals
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      <CursorPagerBar
        pager={pager}
        nextCursor={nextCursor}
        isFetching={isFetching}
        summary={countOf(items.length, "signal")}
      />
    </View>
  );
}

interface SignalRowProps {
  readonly signal: SignalSummary;
  readonly activationHref: (ticketNo: string) => Href;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
}

function SignalRow({ signal, activationHref, siteLabel, cameraLabel }: SignalRowProps) {
  const cameraKey = activationCameraKey(signal);

  return (
    <View style={styles.row}>
      <Text textStyle="xs/regular" color="text" style={styles.received}>
        {formatInstant(signal.receivedDateTime)}
      </Text>
      <Text textStyle="sm/regular" color="text" style={styles.description} numberOfLines={2}>
        {formatText(signal.description)}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.site} numberOfLines={1}>
        {siteLabel(signal.a3sSite)}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.camera} numberOfLines={1}>
        {cameraKey !== undefined ? cameraLabel(cameraKey) : signal.a3sCamera}
      </Text>
      <View style={styles.ticket}>
        {
          signal.ticketNo !== undefined ?
            // A real anchor, so cmd/middle-click opens the activation in a new tab.
            <Link href={activationHref(signal.ticketNo)} asChild>
              <Pressable accessibilityRole="link" style={styles.link}>
                {({ hovered }) => (
                  <Text textStyle="sm/semibold" color="text" style={hovered && styles.hoveredLink}>
                    {signal.ticketNo}
                  </Text>
                )}
              </Pressable>
            </Link>
            // No ticket is normal for a test signal, which never raises one.
          : <Text textStyle="xs/regular" color="text">
              {EMPTY}
            </Text>

        }
      </View>
      <Text textStyle="xs/regular" color="textMuted" style={styles.signalId} numberOfLines={1} selectable>
        {signal.a3sSignalId}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  hoveredLink: { textDecorationLine: "underline" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  received: { width: 180 },
  description: { flex: 2, minWidth: 140 },
  site: { flex: 1, minWidth: 100 },
  camera: { flex: 1, minWidth: 100 },
  ticket: { width: 100 },
  signalId: { flex: 2, minWidth: 160 },
}));
